import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  NodeResizeControl,
  NodeToolbar,
  Position,
  ResizeControlVariant,
  useStoreApi,
  type NodeProps,
  ViewportPortal,
  type Node,
} from '@xyflow/react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { UpdateCanvasNote } from '@/src/renderer/lib/db/models'
import { parseNoteMarkdown, continueListOnEnter } from '../utils/canvasMarkdown'
import {
  getMemoColor,
  DEFAULT_MEMO_MIN_WIDTH,
  DEFAULT_MEMO_MAX_WIDTH,
  MEMO_MIN_HEIGHT,
} from '../utils/canvasMemoStyle'
import { getPlacementSize } from '../utils/canvasPlacement'
import { getCanvasNoteRingColor } from '../utils/canvasNoteStyle'
import { isConverted, isConvertible, isEditedSinceConversion } from '../utils/noteToTask'
import { useMeasuredNoteHeight } from '../hooks/useMeasuredNoteHeight'
import {
  useCanvasPointerBusy,
  useOptionsBarPlacement,
  useSoloSelected,
  useSpawnHovered,
} from '../hooks/useCanvasOptionsBar'
import { useIsPeeling, useIsTapped } from '../hooks/useCanvasTapped'
import { CanvasTape } from '../CanvasTape'
import { canvasTapeIdentity } from '../tapes/canvasTapes'
import { usePresence } from '../hooks/usePresence'
import { useOptionPreview } from '../hooks/useOptionPreview'
import { getCanvasNoteFont } from '../utils/canvasFonts'
import { CanvasOptionsBar } from '../optionsBar'
import type { CanvasOptionsBarApplyState } from '../optionsBar/CanvasOptionsBar.types'
import { OPTIONS_BAR_OFFSET } from '../optionsBar/CanvasOptionsBar.types'
import { CanvasSpawnHandles } from '../CanvasSpawnHandles'
import { RotateHandles } from '../RotateHandles'
import { useRotateDrag } from '../hooks/useRotateDrag'
import { canvasNoteRotations, rotatedBounds } from '../utils/canvasRotation'
import { releaseTextSelection } from '../utils/canvasTextSelection'
import { MemoAuthor } from './MemoAuthor'
import type { MemoNodeData } from './MemoNode.types'
import './MemoNode.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

/**
 * Die **Memo** — die zweite Art von **Canvas-Element** (Figma 4869:94938 und
 * 4869:95003). Siehe „Memo (Canvas)" in CONTEXT.md und
 * `docs/superpowers/specs/2026-08-31-canvas-note-design.md`.
 *
 * **Was sie vom Notizen unterscheidet:** Ihre Höhe folgt dem Text (gezogen
 * wird nur die Breite), sie steht linksbündig statt mittig, ihre Schrift ist
 * fest statt adaptiv, sie kennt Absätze und Listen, und sie hat zwei
 * zuschaltbare Bereiche — Titel und Autor.
 *
 * **Was gleich bleibt:** Auswählen, Verschieben, Einrasten, Duplizieren,
 * Löschen, das **Eselsohr** und die **Übernahme** in Aufgaben. Diese Wege
 * arbeiten auf `CanvasNote` und nicht auf der Art, und sie sollen es bleiben.
 *
 * @example
 * <ReactFlow nodeTypes={{ memo: MemoNode }} … />
 */
export const MemoNode: React.FC<NodeProps<Node<MemoNodeData>>> = ({
  data,
  selected,
  width: nodeWidth,
  height: nodeHeight,
  // Die Lage auf der Fläche — für die Anlegepunkte, die außerhalb dieses
  // Nodes gezeichnet werden und deshalb Canvas-Koordinaten brauchen.
  positionAbsoluteX,
  positionAbsoluteY,
}) => {
  const { t } = useTranslation()
  const dataSource = useDataSource()
  const { note, startEditing = false, editingEnabled = true, onResizeStart, onEdit, onDelete, onOpenTask, onSpawnHover, onSpawn, onApply, editRequest, onEditingChange, onTapeClick, onTape, onRetape, onUntape, onPeeled, onAngle, colorPreview, fontPreview } = data

  /*
    Schreiben geht über `CanvasFlow` — dort liegt der Rückgängig-Stapel.
    Fehlen die Rückrufe (Story, Test), schreibt das Memo selbst: Sie soll
    ohne den Canvas nicht kaputtgehen, nur eben ohne Schritt. Dasselbe Muster
    wie im `NoteNode`.
  */
  const edit = useCallback((id: string, before: UpdateCanvasNote, after: UpdateCanvasNote) => {
    if (onEdit) onEdit(id, before, after)
    else void dataSource.updateCanvasNote(id, after)
  }, [onEdit, dataSource])

  const converted = isConverted(note)
  const editedSince = isEditedSinceConversion(note)

  /*
    Ob das **Eselsohr** knicken DARF — dieselbe Regel wie im `NoteNode`.

    Beim ersten Zeichnen nicht: Eine schon übernommene Memo trägt die Ecke
    als Zustand, nicht als Ereignis. Einmal scharf, bleibt es scharf — das
    Rückgängig klappt die Ecke wieder auf, das Alt-Ziehen lässt sie sichtbar
    auf den zurückbleibenden Zettel wandern.
  */
  /*
    Beim Rendern verglichen, NICHT in einem Effekt.

    Ein Effekt läuft erst nach dem Zeichnen: Der Knick stünde bereits fertig
    da, und die Transition käme einen Durchlauf zu spät — sie hätte nichts
    mehr zu überblenden. Genau so knallte das Eselsohr beim Übernehmen
    schlagartig heraus, statt hereinzurollen.

    Die Zustandsanpassung während des Renderns lässt React sofort noch einmal
    durchlaufen, bevor irgendetwas am Bildschirm ankommt. Beide Klassen
    erreichen die Hülle damit im SELBEN Schritt — und eine Transition, die
    zusammen mit der Wertänderung scharf wird, läuft: Der Browser liest die
    Übergangsregeln aus dem Zustand NACH der Änderung, den alten Wert kennt er
    noch.
  */
  const [seenConverted, setSeenConverted] = useState(converted)
  const [foldAnimates, setFoldAnimates] = useState(false)
  if (converted !== seenConverted) {
    setSeenConverted(converted)
    setFoldAnimates(true)
  }

  /**
   * Klick auf das Ohr — zur Aufgabe. Dieselben drei Handler wie im
   * `NoteNode`.
   *
   * `stopPropagation` auf dem `pointerdown`: Sonst fasste React Flow den
   * Zettel an und der Klick würde zum Anfang eines Verschiebens.
   */
  const handleEarPointerDown = useCallback((event: React.PointerEvent) => {
    event.stopPropagation()
  }, [])
  /**
   * Der Doppelklick ebenso: Er steigt sonst zum Zettel auf und öffnete den
   * **Bearbeiten-Modus** — hinter dem Sprung, den der erste Klick schon
   * ausgelöst hat.
   */
  const handleEarDoubleClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
  }, [])
  const handleEarClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    if (note.taskId) onOpenTask?.(note.taskId)
  }, [note.taskId, onOpenTask])

  const [content, setContent] = useState(note.content)
  // Der Titel bekommt einen eigenen Entwurf: Er wird zusammen mit dem Text
  // im selben Bearbeiten-Modus geschrieben, aber getrennt festgeschrieben —
  // ändert sich nur einer der beiden, soll auch nur für den ein Schritt
  // entstehen.
  const [titleDraft, setTitleDraft] = useState(note.title)
  /*
    Der zuletzt FESTGESCHRIEBENE Titel — damit ein versehentliches Ausschalten
    ihn nicht verschluckt: Wer gleich wieder einschaltet, bekommt zurück, was
    dastand (gemeldet 2026-09-05, dieselbe Regel am **Bereich**). Ein
    verworfener Entwurf zählt nicht mit; er wurde nie geschrieben.
  */
  const lastTitleRef = useRef('')
  if (note.title) lastTitleRef.current = note.title
  const [isEditing, setIsEditing] = useState(startEditing)
  const editorRef = useRef<HTMLTextAreaElement>(null)
  const titleInputRef = useRef<HTMLInputElement>(null)

  // Beginn und Ende melden; beim Verschwinden des Nodes gilt „nicht mehr".
  const onEditingChangeRef = useRef(onEditingChange)
  onEditingChangeRef.current = onEditingChange
  useEffect(() => {
    onEditingChangeRef.current?.(note.id, isEditing)
    return () => { if (isEditing) onEditingChangeRef.current?.(note.id, false) }
  }, [isEditing, note.id])
  /*
    Doppelklick auf den TITEL: Der Fokus soll im Titel landen, nicht im Text
    (CONTEXT.md, Memo). Das `<textarea>` holt ihn sich beim Aufbau per
    `autoFocus`; dieser Effekt läuft danach und zieht ihn weiter, wenn der
    Doppelklick es vorgemerkt hat. Ein Ref statt State: Es ist eine
    Absicht für den nächsten Aufbau, kein Zustand, der gerendert wird.
  */
  const focusTitleRef = useRef(false)
  useEffect(() => {
    if (!isEditing || !focusTitleRef.current) return
    focusTitleRef.current = false
    titleInputRef.current?.focus({ preventScroll: true })
  }, [isEditing])
  const store = useStoreApi()

  /*
    Die Bitte von außen (`editRequest`, siehe Typ): bei jeder Erhöhung öffnen.
    Der Anfangswert zählt nicht — sonst ginge ein Zettel, dessen Node mit
    einer alten Bitte neu aufgebaut wird, ungefragt auf.

    Und AUSWÄHLEN, wie der Doppelklick auf den Zettel selbst: Der Ring liegt
    auch auf einem nur überfahrenen Zettel, und „abgewählt heißt nicht im
    Bearbeiten-Modus" schlösse den Text sonst im selben Atemzug wieder.
  */
  const editRequestRef = useRef(editRequest)
  useEffect(() => {
    if (editRequest === editRequestRef.current) return
    editRequestRef.current = editRequest
    if (!editRequest || !editingEnabled) return
    store.getState().addSelectedNodes([note.id])
    setIsEditing(true)
  }, [editRequest, editingEnabled, store, note.id])

  /*
    Der Doppelklick wählt aus UND öffnet — wie bei der Notiz: Die Auswahl
    schrumpft auf diese Memo (`addSelectedNodes` ersetzt sie), und die harte
    Regel „abgewählt heißt nicht im Bearbeiten-Modus" findet im Store sofort
    eine ausgewählte Memo vor.
  */
  const openEditor = useCallback(() => {
    if (!editingEnabled) return
    // Ein geklebter Zettel wird nicht bearbeitet — siehe `NoteNode`:
    // Der Doppelklick blubbert vom **Streifen** herauf, der als Griff seine
    // Ereignisse behält.
    if (note.tape) return
    store.getState().addSelectedNodes([note.id])
    setIsEditing(true)
  }, [editingEnabled, store, note.id, note.tape])

  const handleTitleDoubleClick = useCallback(() => {
    if (!editingEnabled || note.tape) return
    focusTitleRef.current = true
    openEditor()
  }, [editingEnabled, openEditor, note.tape])

  // Vom Datensatz spiegeln — aber nie mitten im Tippen, sonst setzte die
  // Live-Query den Text auf den alten Stand zurück, während der
  // Schreibvorgang noch unterwegs ist. Dieselbe Falle wie bei der Notiz.
  const isEditingRef = useRef(isEditing)
  isEditingRef.current = isEditing
  useEffect(() => {
    if (!isEditingRef.current) setContent(note.content)
  }, [note.content])
  useEffect(() => {
    if (!isEditingRef.current) setTitleDraft(note.title)
  }, [note.title])

  // Die Vorschau des Farbwählers färbt das Memo — Leiste und Verlaufsschritt lesen `note.color`.
  const [ownPreview, setOwnPreview] = useOptionPreview(note.color)
  const shownColor = (ownPreview ?? colorPreview)?.color ?? note.color
  const color = getMemoColor(shownColor)
  // Die Vorschau des Schriftmenüs; die Höhenmessung folgt ihr (Plan, Entscheidung 4).
  const [ownFontPreview, setOwnFontPreview] = useOptionPreview<{ font: string | null }>(note.font)
  const fontShown = ownFontPreview ?? fontPreview
  const font = getCanvasNoteFont(fontShown ? fontShown.font : note.font)

  /*
    Alle drei rechnen nur für den ausgewählten Zettel: Ihre Selektoren laufen
    bei jeder Store-Änderung, und React Flow schreibt beim Ziehen in jedem
    Frame. Ein nicht ausgewählter Zettel zeigt ohnehin keine Leiste.
  */
  /*
    Der FRISCHE Zettel: Er startet im Bearbeiten-Modus, ohne ausgewählt zu sein,
    und zeigt als einzige Ausnahme die Leiste trotzdem — sofort, nicht erst
    nach der Wartezeit einer Geste (CONTEXT.md, Bearbeiten-Modus). Sobald
    dieser erste Bearbeiten-Modus endet, ist er ausgewählt (`commitContent`),
    und die gewöhnliche Regel gilt.
  */
  const fresh = startEditing && isEditing && !selected
  const soloSelected = useSoloSelected(selected)
  const pointerBusy = useCanvasPointerBusy(selected)
  // Der **Klebestreifen** — siehe `NoteNode` und `useCanvasTapped`:
  // Geklebt wird nicht ausgewählt, sondern **angetippt**.
  const taped = Boolean(note.tape)
  const tapped = useIsTapped(note.id)
  const peeling = useIsPeeling(note.id)
  /*
    Die **Neigung** und ihr Überstand — dieselbe Rechnung wie am Zettel
    (siehe `NoteNode`). Das Memo trifft sie härter: Es wird breiter und höher,
    und ein 240px hohes Memo hebt seine obere Ecke bei 42° um 49px — mehr als
    die 40 des `OPTIONS_BAR_OFFSET`.
  */
  const rotation = note.rotation ?? 0
  const rotationLift = (rotatedBounds(
    nodeWidth || note.width || getPlacementSize('memo').width,
    nodeHeight || note.height || getPlacementSize('memo').height,
    rotation,
  ).height - (nodeHeight || note.height || getPlacementSize('memo').height)) / 2

  const placement = useOptionsBarPlacement(note.y - rotationLift, selected || fresh || tapped)
  /*
    Steht die Vorschau eines **Anlegepunkts**, weicht die Leiste — am oberen
    und linken Punkt rückt der Zettel dabei selbst, und eine Leiste, die
    mitwanderte, führe quer über die gerückte Reihe. Der Merker hängt an den
    Punkten (siehe `useSpawnHovered`) und liegt im Node, nicht in `data`: Der
    Abgleich in `CanvasFlow` vergleicht nur `note`, `startEditing` und
    `editingEnabled` — ein neues Datenfeld bliebe in alten Nodes stehen.
  */
  /*
    **Ein geklebter Zettel trägt keine Anlegepunkte** (CONTEXT.md,
    Klebestreifen). Er ist aus dem Spiel — auch als Ausgangspunkt: Was er noch
    kann, ist gelesen und abgelöst zu werden.
  */
  const spawnHandlesShown = Boolean(
    !taped && selected && soloSelected && editingEnabled && !isEditing && onSpawn,
  )
  const [spawnHovered, setSpawnHovered] = useSpawnHovered(spawnHandlesShown)
  const showBar = taped
    ? tapped
    : fresh || (selected && soloSelected && !pointerBusy && !spawnHovered)
  // Sanft raus wie rein: kurz stehen lassen und abblenden (siehe `usePresence`).
  const barPresence = usePresence(showBar)

  // Wie am Notizen: Wer den Text abschließt, hat den Zettel weiter in der
  // Hand — er ist danach ausgewählt (CONTEXT.md, Bearbeiten-Modus).

  // Der Fokus zurück nach einer Aktion in der Leiste — siehe `NoteNode`.
  const rootRef = useRef<HTMLDivElement | null>(null)
  const returnFocus = useCallback(() => {
    const target = isEditing
      ? editorRef.current
      : rootRef.current?.closest<HTMLElement>('.react-flow__node')
    target?.focus({ preventScroll: true })
  }, [isEditing])
  // `getPlacementSize('memo').width`, nicht `DEFAULT_MEMO_MIN_WIDTH`: Die
  // Untergrenze fürs Ziehen (`DEFAULT_MEMO_MIN_WIDTH`, unten bei
  // `minWidth`) und die Antwort auf „wie breit ist ein Memo ohne eigene
  // Breite" sind zwei verschiedene Fragen, die zufällig denselben Wert (168)
  // hatten und in `CanvasFlow.noteToFlowNode` dieselbe Frage dritt noch
  // anders beantworteten — vereinheitlicht auf die EINE Stelle, die auch das
  // Absetzen entscheidet.
  const width = nodeWidth || note.width || getPlacementSize('memo').width

  /*
    ── **Neigung** ──────────────────────────────────────────────────────────

    Wie am Zettel: vier Zonen schräg außerhalb der Ecken, gedreht wird um die
    **Mitte**. Dieselbe Geste, dieselben Rasten (7°-Stufen von −42 bis 42),
    dasselbe Schreiben über `edit`.

    **Die Höhenmessung bleibt heil.** `useMeasuredNoteHeight` misst
    `offsetHeight`, und `offsetHeight` kennt keine Transformation — die Höhe
    des Memos ändert sich durch die Neigung also nicht. Mit
    `getBoundingClientRect` wäre es anders: Die läse den gedrehten Kasten, das
    Memo wüchse mit jedem Grad, die Messung schriebe zurück, und das Spiel
    begänne von vorn.
  */
  const showRotate = Boolean(selected) && soloSelected && editingEnabled && !isEditing
  const rotate = useRotateDrag({
    getCenter: () => {
      const box = rootRef.current?.getBoundingClientRect()
      return box ? { x: box.left + box.width / 2, y: box.top + box.height / 2 } : null
    },
    getRasten: canvasNoteRotations,
    getStart: () => rotation,
    onCommit: angle => edit(note.id, { rotation }, { rotation: angle }),
    onReset: () => { if (rotation !== 0) edit(note.id, { rotation }, { rotation: 0 }) },
    enabled: showRotate,
  })
  const shownRotation = rotate.preview ?? rotation

  /*
    Die Höhe wird gemessen und zurückgeschrieben — sie steht in der Datenbank,
    weil Einrasten, Maßklammern und die Randpfeile mit ihr rechnen, aber das
    Layout richtet sich nach der Messung. Ausführlich bei `useMeasuredNoteHeight`.
  */
  const measureRef = useMeasuredNoteHeight({
    noteId: note.id,
    storedHeight: note.height,
    enabled: !isEditing,
  })

  /*
    **Abgewählt heißt: nicht im Bearbeiten-Modus** (CONTEXT.md; Begründung bei
    `NoteNode`): Der Klick auf die Fläche löst kein `blur` aus. Harte
    Regel bei JEDEM Render ohne Auswahl — Ausnahme die frisch abgesetzte
    Memo, die noch nie ausgewählt war.
  */
  const commitContentRef = useRef<() => void>(() => {})
  const everSelectedRef = useRef(Boolean(selected))
  if (selected) everSelectedRef.current = true
  useEffect(() => {
    // Die Wahrheit über die Auswahl steht im Store — die Prop kann einen
    // Render hinterherhinken (Doppelklick wählt aus UND öffnet im selben Tick).
    if (selected || store.getState().nodeLookup.get(note.id)?.selected) return
    if (startEditing && !everSelectedRef.current) return
    if (isEditingRef.current) commitContentRef.current()
  }, [selected, isEditing, startEditing, store, note.id])

  /*
    Der EINE Ausstieg aus dem Bearbeiten-Modus. Räumt pro forma Textauswahl
    und Textfokus beider Felder ab (`releaseTextSelection`) — ohne Bedingung.
    Der Riegel hält den `blur` ab, den das Abräumen selbst auslöst
    (`handleFieldBlur` → hierher), sonst würde zweimal festgeschrieben.
  */
  const committingRef = useRef(false)
  const commitContent = useCallback(() => {
    if (committingRef.current) return
    committingRef.current = true
    try {
      setIsEditing(false)
      store.getState().addSelectedNodes([note.id])
      if (content !== note.content) edit(note.id, { content: note.content }, { content })
      if (titleDraft !== note.title) edit(note.id, { title: note.title }, { title: titleDraft })
      // Festgeschrieben heißt: erinnert (siehe `lastTitleRef`).
      if (titleDraft) lastTitleRef.current = titleDraft
      releaseTextSelection(editorRef.current)
      releaseTextSelection(titleInputRef.current)
    } finally {
      committingRef.current = false
    }
  }, [content, note.content, note.id, edit, titleDraft, note.title, store])
  commitContentRef.current = commitContent

  /*
    Titel an/aus. Der ENTWURF ist die Wahrheit, nicht die Prop (siehe
    `titleOn`), und er geht mit aus: Sonst schriebe ein späteres
    Festschreiben den ausgeschalteten Titel wieder hin.

    Das „Vorher" für **Rückgängig** ebenso: Beim Einschalten ist es `null` —
    genau das heißt „aus" —, beim Ausschalten der Entwurf, der unsere eigenen
    Schreibvorgänge schon kennt; im Bearbeiten-Modus die Prop, weil der
    Entwurf dort ungeschriebenen Text trägt.
  */
  const handleToggleTitle = useCallback(() => {
    if (titleDraft === null) {
      const restored = lastTitleRef.current
      setTitleDraft(restored)
      edit(note.id, { title: null }, { title: restored })
      return
    }
    setTitleDraft(null)
    edit(note.id, { title: isEditingRef.current ? note.title : titleDraft }, { title: null })
  }, [titleDraft, note.title, note.id, edit])

  const applyState: CanvasOptionsBarApplyState = isConverted(note) ? 'done' : isConvertible(note) ? 'ready' : 'empty'
  // Übernehmen beendet zuerst den Bearbeiten-Modus (CONTEXT.md).
  const handleApply = useCallback(() => {
    if (isEditingRef.current) commitContentRef.current()
    onApply?.(note.id)
  }, [onApply, note.id])

  /**
   * Der gemeinsame `blur`-Wächter für Text UND Titel.
   *
   * **Geprüft, nicht vermutet:** Mit `fireEvent.blur(el, { relatedTarget })`
   * nachgestellt, was ein echter Fokuswechsel im Browser auslöst — ein Klick
   * von der Textarea in den Titel (oder umgekehrt) feuert AUF BEIDEN
   * Feldern ein eigenes `blur`, bevor das andere den Fokus überhaupt
   * bekommt. Ein `onBlur={commitContent}` je Feld schriebe dort also fest
   * UND beendete den Bearbeiten-Modus, während der Zeiger noch innerhalb
   * derselben Memo unterwegs ist — genau der Fehler, den React Testing
   * Library mit `fireEvent.doubleClick` → `fireEvent.blur(textarea)` ohne
   * jede Eingabe reproduziert hat.
   *
   * Der Ausweg: `event.relatedTarget` ist das Element, das den Fokus als
   * NÄCHSTES bekommt (MDN `FocusEvent.relatedTarget`, von jsdom und allen
   * Browsern gesetzt). Liegt es noch innerhalb `.memo-node`, bleibt der
   * Bearbeiten-Modus offen — erst ein Ziel außerhalb des Memos (oder gar
   * keins, weil der Fokus ins Nichts fällt, etwa bei Escape/Blur per Skript)
   * schreibt fest.
   */
  const handleFieldBlur = useCallback((event: React.FocusEvent<HTMLElement>) => {
    const container = event.currentTarget.closest('.memo-node')
    const next = event.relatedTarget
    if (container && next instanceof Node && container.contains(next)) return
    commitContent()
  }, [commitContent])

  /**
   * Enter in einer Liste setzt den nächsten Strich (siehe
   * `continueListOnEnter`). Nur dort greift die Hilfe — sonst gilt Enter wie
   * überall und macht einen Umbruch.
   */
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape' || (event.key === 'Enter' && (event.metaKey || event.ctrlKey))) {
      event.preventDefault()
      /*
        Hinaus über `blur`, nicht direkt über `commitContent`: Der Fokus soll
        danach auf dem NODE liegen (wie bei der Notiz), damit Entf am Canvas
        ankommt. Erst festschreiben und dann den Node fokussieren feuerte das
        `blur` des noch gemounteten Feldes ein zweites Mal — und damit einen
        zweiten Undo-Schritt. So läuft alles einmal: `blur` → `handleFieldBlur`
        (kein `relatedTarget`, also festschreiben) → Fokus auf den Node.
      */
      event.currentTarget.blur()
      rootRef.current?.closest<HTMLElement>('.react-flow__node')?.focus({ preventScroll: true })
      return
    }
    if (event.key !== 'Enter' || event.shiftKey) return

    const editor = event.currentTarget
    const next = continueListOnEnter(editor.value, editor.selectionStart)
    if (!next) return

    event.preventDefault()
    setContent(next.content)
    // Nach dem Zeichnen: Der Wert steht erst dann im Feld, und die
    // Zeigerstelle gehört an das Ende des neuen Strichs.
    requestAnimationFrame(() => {
      editor.setSelectionRange(next.caret, next.caret)
    })
  }, [])

  const blocks = parseNoteMarkdown(content)

  return (
    <>
      {/*
        **Keine Ziehpunkte, nur die beiden senkrechten Kanten.**

        Beim **Notizen** skalieren die vier Ecken den Zettel MIT seiner Schrift
        (die ist adaptiv). Hier ist die Schrift fest — es gibt nichts diagonal
        zu skalieren, und vier Punkte versprächen etwas, das das Memo nicht
        einlöst.

        `variant={ResizeControlVariant.Line}` (Wert `"line"`) und nicht
        `"handle"`: Die ganze Kante ist Ziehfläche. Ein Punkt auf halber Höhe
        wanderte beim Wachsen des Textes ständig, und eine Kante ist ohnehin
        leichter zu treffen.

        Geprüft an `@xyflow/system` 12.11.3 (`xyresizer/types.ts`,
        `getControlDirection`): `position="left"`/`"right"` markiert die
        Kante als NUR horizontal (`isHorizontal = position.includes('left'
        | 'right')`, `isVertical = position.includes('top' | 'bottom')`) —
        die vertikale Distanz wird beim Ziehen fest auf 0 gerechnet; kein
        zusätzliches `resizeDirection` nötig. Das gilt für React Flow SELBST —
        `sizing.dimensions.height` kommt während des Zugs unverändert herein.

        Für die Ecke, die hier unten gemeldet wird (`onResizeStart`), stimmt
        die Behauptung „wächst hier ausschließlich die Breite" erst seit
        `getWidthResizeAlignment` (`utils/canvasAlignment.ts`) auch für die
        Pipeline DAHINTER: Vorher lief jeder Zug hier über `getResizeAlignment`
        — seitenverhältnistreu, für die Notiz gebaut —, und dessen
        `height = width / aspect` schrieb bei jedem Breitenzug eine erfundene
        Höhe und ein daraus verschobenes `y` in die Datenbank, obwohl React
        Flow selbst nie eine Höhenänderung gemeldet hatte.
      */}
      {/*
        Die vier **Anlegepunkte** — im `ViewportPortal`, wie die Ziehpunkte
        der Notiz: Aus dem Node heraus kämen sie nicht über die Hilfslinie.
        Nur am EINZELN ausgewählten Zettel, nicht beim Bearbeiten.
      */}
      {spawnHandlesShown && (
        <ViewportPortal>
          <CanvasSpawnHandles
            rect={{
              x: positionAbsoluteX,
              y: positionAbsoluteY,
              width,
              height: nodeHeight || note.height || getPlacementSize('memo').height,
            }}
            suspended={pointerBusy}
            onHover={side => {
              setSpawnHovered(side !== null)
              onSpawnHover?.(note.id, side)
            }}
            onSpawn={side => {
              // Der Klick beendet die Geste — auch wenn dieser Zettel
              // ausgewählt bleibt, weil `handleSpawn` früh zurückkehrt
              // (kein Platz, laufende Geste, offenes Anlegen).
              setSpawnHovered(false)
              onSpawn?.(note.id, side)
            }}
          />
        </ViewportPortal>
      )}

      {/*
        Die vier **Dreh-Zonen** — schräg außerhalb der Ecken, unsichtbar, der
        Cursor ist die Ansage. Am Memo an denselben vier Ecken wie am Zettel,
        obwohl dort NICHT skaliert wird (das Memo hat nur die beiden
        senkrechten Kanten): Die Ecke ist die Stelle, an der eine Hand ein Blatt
        dreht, ganz gleich was sonst dort liegt.
      */}
      <RotateHandles shown={showRotate} handlers={rotate.handlers} />

      {/*
        **Die Kanten weichen, solange gedreht wird** — dasselbe wie die
        Ziehpunkte am Zettel: Zwei senkrechte Linien, die auf dem ungedrehten
        Rechteck stillstehen, während das Papier darunter läuft, lesen sich als
        Fehler.
      */}
      {selected && editingEnabled && !isEditing && !rotate.rotating && (['left', 'right'] as const).map(side => (
        <NodeResizeControl
          key={side}
          position={side}
          variant={ResizeControlVariant.Line}
          className="memo-node__edge"
          minWidth={DEFAULT_MEMO_MIN_WIDTH}
          maxWidth={DEFAULT_MEMO_MAX_WIDTH}
          onResizeStart={() => onResizeStart?.(note.id, side === 'left' ? 'top-left' : 'top-right', 'edge')}
        />
      ))}

      {/*
        Die **Options-Leiste**. Sie erscheint nur, wenn dieser Zettel der
        EINZIGE ausgewählte ist — ab zwei gehört sie dem **Auswahlrahmen** —
        und weicht jeder laufenden Geste.
      */}
      <NodeToolbar
        isVisible={barPresence.mounted}
        position={placement === 'below' ? Position.Bottom : Position.Top}
        offset={OPTIONS_BAR_OFFSET + rotationLift}
      >
        <CanvasOptionsBar
          leaving={barPresence.leaving}
          onActionDone={returnFocus}
          buttons={taped ? ['untape', 'retape'] : ['emoji', 'color', 'font', 'title', 'author', 'apply', 'tape', 'delete']}
          colorKeys={[note.color]}
          fontKeys={[note.font]}
          /*
            `null` heißt „kein Titel", ein String (auch der leere) heißt an —
            gelesen am ENTWURF, nicht an der Prop: Die Live-Query zieht erst
            nach, und wer schnell zweimal drückte, schaltete sonst zweimal aus.
          */
          titleOn={titleDraft !== null}
          authorOn={note.showAuthor}
          placement={placement}
          onColorChange={key => edit(note.id, { color: note.color }, { color: key })}
          onColorPreview={setOwnPreview}
          onFontChange={key => edit(note.id, { font: note.font }, { font: key })}
          onFontPreview={setOwnFontPreview}
          onToggleTitle={handleToggleTitle}
          onToggleAuthor={() => edit(note.id, { showAuthor: note.showAuthor }, { showAuthor: !note.showAuthor })}
          onDelete={() => (onDelete ? onDelete(note.id) : void dataSource.deleteCanvasNote(note.id))}
          onTape={onTape ? () => onTape(note.id) : undefined}
          onRetape={onRetape ? () => onRetape(note.id) : undefined}
          onUntape={onUntape ? () => onUntape(note.id) : undefined}

          onApply={onApply ? handleApply : undefined}
          applyState={applyState}
        />
      </NodeToolbar>

      <div
        ref={element => { measureRef(element); rootRef.current = element as HTMLDivElement | null }}
        className={
          'memo-node'
          + (isEditing ? ' memo-node--editing' : '')
          + (converted ? ' memo-node--converted' : '')
          + (foldAnimates ? ' memo-node--fold-animates' : '')
          + (taped ? ' memo-node--taped' : '')
        }
        style={{
          width,
          // Leer so hoch wie eine Notiz, von dort wächst sie nach unten.
          // Als MINDESTmaß und nicht als Höhe — sonst schnitte sie langen Text ab.
          minHeight: MEMO_MIN_HEIGHT,
          /*
            Die **Neigung** — wie am Zettel: Daten von hier, Rezept in der
            SCSS. **Im Bearbeiten-Modus richtet sich das Memo auf**; dort ist
            es ein Textfeld, und ein Textfeld liegt gerade. Hier und nicht in
            der SCSS, weil eine Inline-Variable jede Regel schlägt.
          */
          '--note-angle': `${isEditing ? 0 : shownRotation}deg`,
          '--note-surface': color.surface,
          // Der satte Ton DIESER Memo — derselbe, den ihr **Tauschpunkt**
          // trägt, und aus demselben Grund wie am Notizen: Aus der Fläche
          // gerechnet wäre er grau statt farbig. Für den **Umriss** beim
          // **Platz tauschen** (siehe SCSS).
          '--note-edge': getCanvasNoteRingColor(shownColor),
          // Nur Weiß und Grau bringen ihn mit; sonst wird er aus der Fläche
          // gerechnet (siehe `TextNoteColor.fold`). Der Hover-Ton geht als
          // EIGENE Variable mit — die SCSS greift ihn in ihrer Hover-Regel
          // ab, weil eine Inline-Variable jede Regel schlüge.
          ...(color.fold ? { '--note-fold-tint': color.fold } : {}),
          ...(color.foldHover ? { '--note-fold-tint-hover': color.foldHover } : {}),
          '--note-border': color.border,
          '--note-text': color.text,
          // Der **Streifen** am linken Rand und seine Trennlinie. Beide
          // stehen in der Palette und werden nicht gerechnet — anders als
          // der Knick des Eselsohrs, der nur EINEN Ton braucht und ihn
          // deshalb aus der Fläche ableiten kann.
          '--note-stripe': color.stripe,
          '--note-stripe-line': color.stripeLine,
          // Die gewählte Schrift — Titel wie Text. Als Variablen und nicht als
          // Klassen: Es sind Werte aus einer Tabelle, keine Zustände.
          '--note-font': font.stack,
          '--note-font-weight': font.weight,
          // Der Titel steht schwerer — wie viel, hängt an der Schrift
          // (`CANVAS_NOTE_FONTS.titleWeight`).
          '--note-title-weight': font.titleWeight,
          // Der optische Ausgleich: Zwei Schriften in derselben `font-size`
          // sind nicht gleich groß zu SEHEN (siehe `CANVAS_NOTE_FONTS.scale`).
          '--note-font-scale': font.scale,
        } as React.CSSProperties}
        onDoubleClick={openEditor}
      >
        {/*
          Die FLÄCHE — Papier, Rahmen und Schatten. Ein eigenes Element, weil
          der Schnitt fürs **Eselsohr** und der Schatten zwei Ebenen brauchen:
          `filter` und `clip-path` am selben Element löschten einander aus
          (ausführlich in der SCSS). Es malt nichts, was gelesen wird, und
          nimmt keine Zeiger an.
        */}
        <span className="memo-node__surface" aria-hidden="true" />

        {/*
          Das **Eselsohr**: die umgeknickte Ecke ist Sache der SCSS (ein
          `clip-path` auf der Fläche plus ein Verlauf für die Diagonale),
          hier steht nur ihr Ziel — der Weg zurück zur Aufgabe. Dasselbe
          Vorgehen wie im `NoteNode`.

          Ein `<button>` und kein `div`: Er ist über die Tastatur erreichbar
          und trägt seinen Namen selbst. `nodrag` hält React Flow davon ab,
          aus dem Druck ein Verschieben zu machen.
        */}
        {/* Der **Klebestreifen** — siehe `NoteNode`. */}
        {taped && onTapeClick && (
          <CanvasTape key={canvasTapeIdentity(note.tape)} noteId={note.id} tape={note.tape!} tapped={tapped} peeling={peeling} onClick={onTapeClick} onPeeled={onPeeled} onAngle={onAngle} disabled={!editingEnabled} />
        )}

        {converted && <span className="memo-node__fold" aria-hidden="true" />}
        {converted && (
          <WithTooltip content={t('projectCanvas.note.openTask')}>
            <button
              type="button"
              className="memo-node__ear nodrag"
              aria-label={t('projectCanvas.note.openTask')}
              onPointerDown={handleEarPointerDown}
              onDoubleClick={handleEarDoubleClick}
              onClick={handleEarClick}
            />
          </WithTooltip>
        )}

        {/*
          „bearbeitet" — der Text steht nicht mehr so da wie bei der
          Übernahme, und die Aufgabe trägt noch den alten Wortlaut. Sichtbar
          wird es erst bei Auswahl oder Überfahren (SCSS).
        */}
        {editedSince && (
          <WithTooltip content={t('projectCanvas.note.editedHint')}>
            <span className="memo-node__edited">
              {t('projectCanvas.note.edited')}
            </span>
          </WithTooltip>
        )}

        {/*
          Titel und Text teilen sich den Bearbeiten-Modus, werden aber
          getrennt festgeschrieben (siehe `titleDraft` oben). Im Ruhezustand
          steht der Titel als reiner Text da — ein `<input>` hätte hier keinen
          `textContent`, und die Karte soll wie ein Notizzettel zu LESEN sein,
          nicht wie ein Formular.
        */}
        {titleDraft !== null && (
          isEditing ? (
            <input
              ref={titleInputRef}
              className="memo-node__title nodrag"
              value={titleDraft ?? ''}
              placeholder={t('projectCanvas.note.titlePlaceholder')}
              onChange={e => setTitleDraft(e.target.value)}
              onBlur={handleFieldBlur}
            />
          ) : titleDraft ? (
            // Der ENTWURF, nicht `note.title`: Nach dem Loslassen zieht die
            // Datenbank erst mit der Live-Query nach, und für diesen Frame
            // wäre der Titel sonst wieder leer — der Platzhalter flackerte
            // auf. Der Entwurf spiegelt den Datensatz ohnehin, sobald nicht
            // bearbeitet wird (siehe oben). Der Text macht es genauso
            // (`blocks` aus `content`).
            <div className="memo-node__title" onDoubleClick={handleTitleDoubleClick}>{titleDraft}</div>
          ) : (
            // Eingeschaltet, aber leer: auch in Ruhe zu sehen, als derselbe
            // Platzhalter wie im Bearbeiten-Modus. Ein leerer Bereich sagte
            // nicht, dass der Zettel einen Titel hat — man merkte es erst
            // beim Hineinklicken (CONTEXT.md, Memo).
            <div
              className="memo-node__title memo-node__title--placeholder"
              onDoubleClick={handleTitleDoubleClick}
            >
              {t('projectCanvas.note.titlePlaceholder')}
            </div>
          )
        )}

        {isEditing ? (
          /*
            Im Bearbeiten-Modus die ROHFORM: Was man tippt, ist das, was
            gespeichert wird. Ein Feld, das die Striche versteckt und beim
            Speichern wieder erfindet, wäre ohne Editor nicht ehrlich zu
            halten.
          */
          <textarea
            ref={editorRef}
            className="memo-node__editor nodrag"
            value={content}
            autoFocus
            onChange={e => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleFieldBlur}
          />
        ) : (
          <div className="memo-node__body">
            {blocks.map((block, index) => block.kind === 'list' ? (
              <ul key={index} className="memo-node__list">
                {block.items.map((item, i) => (
                  <li key={i} className="memo-node__list-item">{item}</li>
                ))}
              </ul>
            ) : (
              <p key={index} className="memo-node__paragraph">
                {block.lines.join('\n')}
              </p>
            ))}
          </div>
        )}

        {note.showAuthor && <MemoAuthor chipColor={color.chip} textColor={color.text} />}
      </div>
    </>
  )
}
